import { AgAuthenticationService } from '@ag/vc.ag-core/authentication';
import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { AgTrackingService } from '@ag/vc.ag-core/tracking';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { AgWebtemplateConfigurationService } from '@ag/vc.ag-ui';
import { AgLoadingService } from '@ag/vc.ag-ui/loading';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { AnonymousSessionService } from './core/services/anonymous-session.service';
import { SessionService } from './core/services/session.service';
import { MaintenanceUtil } from './maintenance/maintenance-util';
import { UserSimulationService } from './modules/simulation/services/user-simulation.service';
import { DistributionChannel } from './shared/models/distributionChannelType';
import { ResourceModel } from './shared/models/ResourceModel';
import { RouteNames } from './shared/routing/app-routing.constants';
import { appState, AppStateService } from './shared/services/app-state.service';
import { ExternalScriptsLoaderService } from './shared/services/external-scripts-loader.service';
import { HelperService } from './shared/services/helper.service';
import { SilToastrService } from './shared/services/sil-toastr.service';

@Component({
  selector: 'ag-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {
  private prefix = 'https://ag.be/privacy/'
  private resourceDetails: ResourceModel[];
  constructor(
    private readonly agConfiguration: AgWebtemplateConfigurationService,
    private readonly agTranslateService: AgTranslateService,
    private readonly loadingService: AgLoadingService,
    private readonly route: ActivatedRoute,
    private readonly session: SessionService,
    private readonly anonymousSessionService: AnonymousSessionService,
    private readonly titleService: Title,
    private readonly trackingService: AgTrackingService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly silToastr: SilToastrService,
    private readonly scriptLoader: ExternalScriptsLoaderService,
    private readonly usersimulationservice: UserSimulationService,
    private readonly appStateService: AppStateService,
    private readonly helper: HelperService,
    private readonly authenticationService: AgAuthenticationService,
    private oauthService: OAuthService
  ) {
    if (document.URL.includes(RouteNames.login)) {
      this.prefix = 'https://ag.be/privacy/'
    }
    if (this.session?.getUserParams()?.distributionChannel == DistributionChannel.AG) {
      this.prefix = 'https://ag.be/privacy/'
    } else if (this.session?.getUserParams()?.distributionChannel == DistributionChannel.Broker) {
      this.prefix = 'https://ag.be/privacy/'
    }

    this.trackingService.init();
    this.setWebTemplateConfiguration();
    this.setLanguage();
    this.router.events.subscribe(event => {

      if (event instanceof NavigationStart) {
        // this code is to remove the toastr message on route change
        this.silToastr.clearAllToasts();
        if (!event.url.includes(RouteNames.blocked)) {
          // by default help widget will be shown and if it has to hidden, then it will be taken care from respective component
          this.session.setHelpWidgetSubject(true);
        }
      }
    });

    scriptLoader.renderer = renderer
    void scriptLoader.load('omnichannelLib', 'chatWidget', 'signalR', 'AgentAvailability');
  }

  ngOnInit(): void {
    const authenticationConfig = this.session.getAuthConfig();
      this.authenticationService.loadDiscoveryDocumentAndTryLogin(authenticationConfig).then((isAuthenticated) => {
        console.log("Authentication result:", isAuthenticated);
      })
        .catch((error) => {
          console.error('Error during authentication:', error);
        }).finally(() => {
          let isBetaTestingAllowed = false;
          if (AgAppConfigService.get<boolean>('BetaTesting') === true && (this.helper.getParameterByName('betaTester') || this.helper.getSessionValue("betaTester"))) {
            const betaTester = this.helper.getParameterByName('betaTester') ? this.helper.getParameterByName('betaTester') : this.helper.getSessionValue("betaTester");
            const allowedBetaTesters = (JSON.parse(JSON.stringify(AgAppConfigService.get<string[]>('BetaTesters'))) as string[]).map(x => atob(x));
            if (allowedBetaTesters.includes(betaTester)) {
              isBetaTestingAllowed = true;
              this.helper.createSession("betaTester", betaTester);
            }
          }
          this.appStateService.fetchAppState().subscribe((res: appState) => {
            if (isBetaTestingAllowed === false) {
              MaintenanceUtil.callMaintenanceApi(AgAppConfigService.get<string>('maintenanceApiUrl'), res.IsInMaintenance);
            }
          });
          setTimeout(() => {
            this.setPolicyLinksUrl(this.session?.getUserParams()?.language);
            this.getResourceDetails();
          }, 500);
          this.session.hasParamRefreshed().subscribe(_res => {
            this.prefix = this.session.getUserParams().distributionChannel == DistributionChannel.Broker ? 'https://ag.be/privacy/' : 'https://ag.be/privacy/';
            this.setPolicyLinksUrl(this.session.getUserParams().language);
          });
        });
  }

  private getResourceDetails(): void {
    let sessionResourceDetails: ResourceModel[] = this.session.getResourceDetails();
    if (sessionResourceDetails.length == 0) {
      this.usersimulationservice.getResourceFromAPI().subscribe(
        (res: ResourceModel[]) => {
          if (res.length) {
            this.session.setResourceDetails(res);
          }
        }
      );
    }
  }

  changeLanguage(language: string): void {
    this.loadingService.show();
    this.agTranslateService.changeLanguage(language);
    this.setPolicyLinksUrl();
    this.loadingService.hide();
    // this.addScriptForChat(language);
  }

  private setWebTemplateConfiguration() {
    this.agConfiguration.configuration.useFullWidth = false;
    // this.agConfiguration.configuration.isWizardMode = false;
    this.agConfiguration.configuration.logoUrl = 'assets/images/logo-aginsurance.svg';
    this.agConfiguration.configuration.isMenuVisible = false;
    this.agConfiguration.configuration.pageTitle = '';
    this.agConfiguration.configuration.isLanguagesVisible = true;
    this.agConfiguration.configuration.isFooterNavigationVisible = true;
    this.agConfiguration.configuration.gdprDisabled = AgAppConfigService.get<string>('gdprDisabled') === 'true';
    this.setPolicyLinksUrl();
  }

  private setPolicyLinksUrl(language?: string): void {
    const lang = language ?? this.agTranslateService.currentLang.toLowerCase();
    const prefix = this.prefix;

    // Note: Terms of use & Cookies links had to be inversed!
    this.agConfiguration.configuration.privacyLinkLabel = 'Privacy';
    this.agConfiguration.configuration.termsOfUseLinkLabel = 'Cookies';

    this.agTranslateService.get(['Footer.TermsOfUse', 'Footer.Copyright']).subscribe((translations: string) => {
      this.agConfiguration.configuration.gdprFooterLinkLabel = translations['Footer.TermsOfUse'];
      // this.agConfiguration.configuration.copyrightLabel = translations['Footer.Copyright'];
    });
    this.agConfiguration.configuration.privacyLinkUrl = prefix + lang;
    this.agConfiguration.configuration.termsOfUseLinkUrl = (lang === 'nl') ? prefix + lang + '/cookiebeleid' : prefix + lang + '/politique-des-cookies';
    this.agConfiguration.configuration.gdprFooterLinkUrl = this.getTermsOfUseLink(lang);
  }

  private getTermsOfUseLink(language: string): string {
    if (this.session?.getUserParams()?.distributionChannel === DistributionChannel.AG || document.URL.includes(RouteNames.login)) {
      return 'https://www.ag.be/content/dam/life/digital/documents/'
        + language.toLowerCase()
        + '/D0001'
        + language.toUpperCase().substring(0, 1) + '.pdf';
    }
    else {
      return 'https://www.aginsurance.be/Retail/'
        + language.toLowerCase()
        + '/Documents%20-%20KID/00798332200'
        + language.toUpperCase().substring(0, 1) + '.pdf';
    }
  }

  private setLanguage() {
    this.agTranslateService.onLangChange.subscribe((event) => {
      this.agTranslateService.currentLang = event.lang;
      this.agTranslateService.get('Title.Broker').subscribe((res: string) => {
        this.titleService.setTitle(res);
      });
    });

    this.route.queryParams.subscribe(params => {
      if (params.lang && this.agTranslateService.langs.indexOf(params.lang) !== -1) {
        this.changeLanguage(params.lang.toLowerCase());
        this.session.setUserLanguage(params.lang.toLowerCase());
      } else if (this.session && this.session.getUserParams() && this.session.getUserParams().language) {
        this.changeLanguage(this.session.getUserParams().language.toLowerCase() === 'en' ? 'fr' : this.session.getUserParams().language);
      }
    });
    this.anonymousSessionService.getAnonymousSessionLanguage().subscribe(lang => {
      if (lang) {
        this.changeLanguage(lang);
      }
    });
  }
}
